/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTheme } from "styled-components";
import {
  Book1,
  People,
  Teacher,
  Notification,
  MessageQuestion,
  Messages2,
  Personalcard,
  DocumentText,
  BookSquare,
  Category,
  ArchiveBook,
  Add,
  Edit2,
} from "iconsax-react";

import PageHeader from "../../components/PageHeader";
import { useAuth } from "../../hooks/auth";
import useAdminGuard from "../../hooks/useAdminGuard";
import { useProfiles } from "../../hooks/profile";
import {
  PageWrapper,
  DashboardMain,
  DashboardSection,
} from "../../styles/adminPage";

import {
  SectionHeader,
  SectionTitleRow,
  SectionIcon,
  SectionTitle,
  SectionSubtitle,
  ModuleGrid,
  ModuleCardButton,
  ModuleCardIcon,
  ModuleCardBody,
  ModuleCardTitle,
  ModuleCardDesc,
  ModuleCardArrow,
} from "./styles";
import Footer from "../../components/Footer";
import AdComponent from "../../components/AdComponent";
import Breadcrumb from "../../components/Breadcrumb";

interface ModuleCard {
  key: string;
  icon: React.ReactNode;
  title: string;
  description: string;
  route: string;
  color: string;
}

interface CategoryGroup {
  key: string;
  icon: React.ReactNode;
  label: string;
  subtitle: string;
  color: string;
  cards: ModuleCard[];
}

function Manager() {
  const { getAll } = useProfiles();
  const navigate = useNavigate();
  const { user, loadingAuth } = useAuth();
  const theme = useTheme();

  const accent = {
    users: theme.colors.secondary,
    usersDeep: theme.colors.secondaryDark,
    usersSoft: theme.colors.secondaryLight,
    usersMid: theme.colors.secondaryDark,
    communication: theme.colors.success,
    communicationDeep: theme.colors.primaryDarker,
    communicationAlt: theme.colors.primaryDark,
    warning: theme.colors.warning,
    content: theme.colors.info,
    contentDeep: theme.colors.primary,
    contentMid: theme.colors.primaryLight,
  };

  useAdminGuard();

  useEffect(() => {
    async function loadData() {
      if (loadingAuth) return;
      if (!user?.uid || (user.type !== "admin" && !user.isAdmin)) return;

      try {
        await getAll(user.uid);
      } catch (error) {
        console.error("Failed to load dashboard data:", error);
        navigate("/");
      }
    }
    loadData();
  }, [getAll, navigate, user?.uid, user?.type, user?.isAdmin, loadingAuth]);

  const renderCard = (card: ModuleCard, index: number) => (
    <ModuleCardButton
      key={card.key}
      id={`card-${card.key}`}
      type="button"
      onClick={() => navigate(card.route)}
      $cardColor={card.color}
      $delayIndex={index}
    >
      <ModuleCardIcon $cardColor={card.color}>{card.icon}</ModuleCardIcon>
      <ModuleCardBody>
        <ModuleCardTitle>{card.title}</ModuleCardTitle>
        <ModuleCardDesc>{card.description}</ModuleCardDesc>
      </ModuleCardBody>
      <ModuleCardArrow $cardColor={card.color}>›</ModuleCardArrow>
    </ModuleCardButton>
  );

  const categories: CategoryGroup[] = [
    {
      key: "users",
      icon: <People size={18} color={accent.users} variant="Bulk" />,
      label: "Usuários",
      subtitle:
        "Contas do app, profissionais, alunos e professores — alinhado ao fluxo Bilíngua.",
      color: accent.users,
      cards: [
        {
          key: "users_management",
          icon: <People size={24} color={accent.users} variant="Bulk" />,
          title: "Usuários cadastrados",
          description:
            "Listar e gerir contas. Bloquear, editar e-mail, remover.",
          route: "/manager/users",
          color: accent.users,
        },
        {
          key: "profiles",
          icon: (
            <Personalcard size={24} color={accent.usersDeep} variant="Bulk" />
          ),
          title: "Profissionais (perfis)",
          description: "Cadastro de profissionais Bilíngua.",
          route: "/profiles",
          color: accent.usersDeep,
        },
        {
          key: "students",
          icon: <People size={24} color={accent.usersSoft} variant="Bulk" />,
          title: "Alunos",
          description: "Alunos vinculados às turmas.",
          route: "/students",
          color: accent.usersSoft,
        },
        {
          key: "teachers",
          icon: <Teacher size={24} color={accent.usersMid} variant="Bulk" />,
          title: "Professores",
          description: "Professores cadastrados.",
          route: "/teachers",
          color: accent.usersMid,
        },
      ],
    },
    {
      key: "communication",
      icon: (
        <Notification size={18} color={accent.communication} variant="Bulk" />
      ),
      label: "Comunicação",
      subtitle: "Notificações, notícias, sugestões e FAQ.",
      color: accent.communication,
      cards: [
        {
          key: "notifications",
          icon: (
            <Notification
              size={24}
              color={accent.communication}
              variant="Bulk"
            />
          ),
          title: "Notificações",
          description: "Criar e publicar avisos push para os usuários.",
          route: "/notifications",
          color: accent.communication,
        },
        {
          key: "news",
          icon: (
            <DocumentText
              size={24}
              color={accent.communication}
              variant="Bulk"
            />
          ),
          title: "Notícias",
          description: "Avisos e comunicados publicados no app.",
          route: "/news",
          color: accent.communication,
        },
        {
          key: "suggestions",
          icon: (
            <Messages2
              size={24}
              color={accent.communicationAlt}
              variant="Bulk"
            />
          ),
          title: "Sugestões dos usuários",
          description: "Revisar envios de ideias e feedback.",
          route: "/suggestions",
          color: accent.communicationAlt,
        },
        {
          key: "faq",
          icon: (
            <MessageQuestion size={24} color={accent.warning} variant="Bulk" />
          ),
          title: "FAQ",
          description: "Perguntas frequentes exibidas no app.",
          route: "/faq",
          color: accent.warning,
        },
      ],
    },
    {
      key: "sinalario",
      icon: <BookSquare size={18} color={accent.content} variant="Bulk" />,
      label: "Sinalário e conteúdo",
      subtitle: "Verbetes, módulos, aulas e turmas.",
      color: accent.content,
      cards: [
        {
          key: "sinalario_list",
          icon: <BookSquare size={24} color={accent.content} variant="Bulk" />,
          title: "Listar e editar verbetes",
          description: "Palavras, vídeos e descrições do sinalário.",
          route: "/sinalario/list",
          color: accent.content,
        },
        {
          key: "add_verbete",
          icon: <Add size={24} color={accent.contentDeep} variant="Bulk" />,
          title: "Cadastrar novo verbete",
          description: "Novo registro no sinalário.",
          route: "/Add-Verbete",
          color: accent.contentDeep,
        },
        {
          key: "edit_flow",
          icon: <Edit2 size={24} color={accent.contentMid} variant="Bulk" />,
          title: "Editar verbete",
          description: "Abra um item na lista para editar.",
          route: "/sinalario/list",
          color: accent.contentMid,
        },
        {
          key: "modules",
          icon: <Book1 size={24} color={accent.contentDeep} variant="Bulk" />,
          title: "Módulos e atividades",
          description:
            "Organizar módulos, vídeos e cadastrar novas atividades.",
          route: "/modules",
          color: accent.contentDeep,
        },
        {
          key: "lessons",
          icon: (
            <DocumentText size={24} color={accent.contentMid} variant="Bulk" />
          ),
          title: "Aulas",
          description: "Conteúdo das aulas / lições.",
          route: "/lessons",
          color: accent.contentMid,
        },
        {
          key: "classes",
          icon: (
            <Category size={24} color={accent.contentDeep} variant="Bulk" />
          ),
          title: "Turmas e aulas",
          description: "Turmas e formulários de aula/atividade.",
          route: "/classes",
          color: accent.contentDeep,
        },
        {
          key: "repository",
          icon: <ArchiveBook size={24} color={accent.content} variant="Bulk" />,
          title: "Repositório",
          description: "Visualizar vídeos do repositório pessoal.",
          route: "/repository",
          color: accent.content,
        },
      ],
    },
  ];

  return (
    <PageWrapper id="page">
      <PageHeader
        title="Painel de gerenciamento"
        description="Atalhos do painel administrativo Bilíngua — mesmo visual do app."
        type="private"
      />

      <DashboardMain className="dashboard-main">
        <Breadcrumb items={[{ label: "Gerenciamento", active: true }]} />
        {categories.map((cat) => (
          <DashboardSection key={cat.key}>
            <SectionHeader>
              <SectionTitleRow>
                <SectionIcon $color={cat.color}>{cat.icon}</SectionIcon>
                <SectionTitle>{cat.label}</SectionTitle>
              </SectionTitleRow>
              <SectionSubtitle>{cat.subtitle}</SectionSubtitle>
            </SectionHeader>
            <ModuleGrid>{cat.cards.map((c, i) => renderCard(c, i))}</ModuleGrid>
          </DashboardSection>
        ))}
        <AdComponent />
      </DashboardMain>

      <Footer />
    </PageWrapper>
  );
}

export default Manager;
